import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { preferencesDb, type Workspace as SupabaseWorkspace } from '../lib/supabase'

export interface WorkspaceTask {
  id: string
  prompt: string
  status: 'queued' | 'running' | 'completed' | 'failed'
  workspace: string
  createdAt: number
  startedAt?: number
  completedAt?: number
  result?: string
}

export interface Workspace {
  id: string
  name: string
  path: string
  color: string
  tasks: WorkspaceTask[]
}

export interface WorkspaceState {
  workspaces: Map<string, Workspace>
  activeWorkspace: string | null
  globalQueue: WorkspaceTask[]

  addWorkspace: (name: string, path: string) => void
  removeWorkspace: (id: string) => void
  setActiveWorkspace: (id: string) => void
  queueTask: (workspaceId: string, prompt: string) => void
  updateTaskStatus: (taskId: string, status: WorkspaceTask['status'], result?: string) => void
  removeTask: (taskId: string) => void
  getQueuedTasks: () => WorkspaceTask[]
  getRunningTasks: () => WorkspaceTask[]
}

const generateId = () => crypto.randomUUID().slice(0, 8)

// Cloud sync utilities
let workspaceSyncTimeout: ReturnType<typeof setTimeout> | null = null

const syncWorkspacesToCloud = (workspaces: Map<string, Workspace>) => {
  if (workspaceSyncTimeout) clearTimeout(workspaceSyncTimeout)
  workspaceSyncTimeout = setTimeout(async () => {
    try {
      // Only sync workspace metadata, not tasks (tasks are transient)
      const cloudFormat: Record<string, SupabaseWorkspace> = {}
      workspaces.forEach((ws, id) => {
        cloudFormat[id] = {
          id: ws.id,
          name: ws.name,
          path: ws.path,
          color: ws.color,
          createdAt: new Date().toISOString() // Approximate
        }
      })
      await preferencesDb.saveWorkspaces(cloudFormat)
    } catch (e) {
      console.warn('Workspace cloud sync failed:', e)
    }
  }, 1000) // 1s debounce for workspaces
}

const loadWorkspacesFromCloud = async (): Promise<Map<string, Workspace> | null> => {
  try {
    const cloudWorkspaces = await preferencesDb.getWorkspaces()
    if (!cloudWorkspaces || Object.keys(cloudWorkspaces).length === 0) return null

    const result = new Map<string, Workspace>()
    Object.entries(cloudWorkspaces).forEach(([id, ws]) => {
      result.set(id, {
        id: ws.id,
        name: ws.name,
        path: ws.path,
        color: ws.color,
        tasks: [] // Tasks are local only
      })
    })
    return result
  } catch (e) {
    console.warn('Workspace cloud load failed:', e)
    return null
  }
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      workspaces: new Map(),
      activeWorkspace: null,
      globalQueue: [],

      addWorkspace: (name: string, path: string) => {
        const id = generateId()
        const workspace: Workspace = {
          id,
          name,
          path,
          color: '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0'),
          tasks: [],
        }
        set((state) => {
          const workspaces = new Map(state.workspaces)
          workspaces.set(id, workspace)
          syncWorkspacesToCloud(workspaces)
          return {
            workspaces,
            activeWorkspace: state.activeWorkspace || id,
          }
        })
      },

      removeWorkspace: (id: string) => {
        set((state) => {
          const workspaces = new Map(state.workspaces)
          workspaces.delete(id)
          syncWorkspacesToCloud(workspaces)
          const nextActive =
            state.activeWorkspace === id
              ? Array.from(workspaces.keys())[0] || null
              : state.activeWorkspace
          return {
            workspaces,
            activeWorkspace: nextActive,
          }
        })
      },

      setActiveWorkspace: (id: string) => {
        set({ activeWorkspace: id })
      },

      queueTask: (workspaceId: string, prompt: string) => {
        const taskId = generateId()
        const task: WorkspaceTask = {
          id: taskId,
          prompt,
          status: 'queued',
          workspace: workspaceId,
          createdAt: Date.now(),
        }
        set((state) => {
          const workspaces = new Map(state.workspaces)
          const workspace = workspaces.get(workspaceId)
          if (workspace) {
            workspace.tasks.push(task)
          }
          return {
            workspaces,
            globalQueue: [...state.globalQueue, task],
          }
        })
      },

      updateTaskStatus: (taskId: string, status: WorkspaceTask['status'], result?: string) => {
        set((state) => {
          const workspaces = new Map(state.workspaces)
          let taskFound = false

          for (const workspace of workspaces.values()) {
            const task = workspace.tasks.find((t) => t.id === taskId)
            if (task) {
              task.status = status
              if (status === 'running' && !task.startedAt) {
                task.startedAt = Date.now()
              }
              if ((status === 'completed' || status === 'failed') && !task.completedAt) {
                task.completedAt = Date.now()
              }
              if (result !== undefined) {
                task.result = result
              }
              taskFound = true
              break
            }
          }

          if (taskFound) {
            const globalQueue = state.globalQueue.map((t) =>
              t.id === taskId
                ? {
                    ...t,
                    status,
                    startedAt: status === 'running' && !t.startedAt ? Date.now() : t.startedAt,
                    completedAt: (status === 'completed' || status === 'failed') && !t.completedAt ? Date.now() : t.completedAt,
                    result: result !== undefined ? result : t.result,
                  }
                : t
            )
            return { workspaces, globalQueue }
          }

          return state
        })
      },

      removeTask: (taskId: string) => {
        set((state) => {
          const workspaces = new Map(state.workspaces)
          for (const workspace of workspaces.values()) {
            workspace.tasks = workspace.tasks.filter((t) => t.id !== taskId)
          }
          return {
            workspaces,
            globalQueue: state.globalQueue.filter((t) => t.id !== taskId),
          }
        })
      },

      getQueuedTasks: () => {
        return get().globalQueue.filter((t) => t.status === 'queued')
      },

      getRunningTasks: () => {
        return get().globalQueue.filter((t) => t.status === 'running')
      },
    }),
    {
      name: 'controldeck-workspaces',
      partialize: (state) => ({
        workspaces: Array.from(state.workspaces.entries()),
        activeWorkspace: state.activeWorkspace,
        globalQueue: state.globalQueue,
      }),
      merge: (persistedState: unknown, currentState: WorkspaceState): WorkspaceState => {
        const persisted = persistedState as {
          workspaces?: [string, Workspace][]
          activeWorkspace?: string | null
          globalQueue?: WorkspaceTask[]
        }
        return {
          ...currentState,
          workspaces: new Map<string, Workspace>(persisted?.workspaces || []),
          activeWorkspace: persisted?.activeWorkspace || null,
          globalQueue: persisted?.globalQueue || [],
        }
      },
      onRehydrateStorage: () => (state) => {
        if (state) {
          loadWorkspacesFromCloud().then((cloudWorkspaces) => {
            if (cloudWorkspaces && cloudWorkspaces.size > 0) {
              const merged = new Map(state.workspaces)
              cloudWorkspaces.forEach((ws, id) => {
                if (!merged.has(id)) {
                  merged.set(id, ws)
                }
              })
              useWorkspaceStore.setState({ workspaces: merged })
            }
          })
        }
      },
    }
  )
)
